import { Fragment, useContext, useState, useEffect, useMemo } from 'react';
import { Container, Row, Col, Card, CardBody, Table } from 'reactstrap';
import { Breadcrumbs } from '../../../../AbstractElements';
import HeaderCard from '../../../Common/Component/HeaderCard';
import CustomizerContext from '../../../../_helper/Customizer';
import { ApplicationApi, ModuleApi, TaskApi } from '../../../../api';
import { Application, ApplicationName, Code, DateCreated, DateModified, Description, ExternalUrl, Icon, Module, ModuleName, Status, Task, TaskName, View, Visibility } from '../../../../Constant';
import { base_form_fields, code_field, createDropdownField, external_url_field, icon_field, visibility_field } from '../../../../Data/FormField/PublicData';
import useAxiosPrivate from '../../../../Hooks/useAxiosPrivate';
import { toast } from 'react-toastify';
import ViewModal from '../../../Common/Component/ViewModal';
import { formatDateWithTimeZone } from '../../../../_helper/dateHelper';
import DataTableComponent from '../../../Common/Component/DataTableComponent';

const TaskList = () => {
  const { layoutURL } = useContext(CustomizerContext);
  const axiosPrivate = useAxiosPrivate()

  const [allData, setAllData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [editMode, setEditMode] = useState(false);

  const [formFields, setFormFields] = useState(base_form_fields(`${TaskName}`));
  const [isNewFieldAdded, setIsNewFieldAdded] = useState(false);

  const ApplicationDropDown = createDropdownField(axiosPrivate,
    'application_id',
    'application_id',
    `${ApplicationName}`,
    `${ApplicationApi}`,
    '', 12
  );
  const ModuleDropDown = createDropdownField(axiosPrivate,
    'module_id',
    'module_id',
    `${ModuleName}`,
    `${ModuleApi}`,
    '', 12
  );

  useEffect(() => {

    const fetchData = async () => {
      try {
        if (!isNewFieldAdded) {
          
          const indexOfName = formFields.findIndex((formField) => formField.id === 'name');
          const indexOfDesc = formFields.findIndex((formField) => formField.id === 'description');
    
          const updatedFields = [ApplicationDropDown, ModuleDropDown, ...formFields];
          updatedFields.splice(indexOfName + 3, 0, code_field());
          updatedFields.splice(indexOfDesc + 4, 0, icon_field(), external_url_field(), visibility_field);
          
  
          await updatedFields[0].fetchOptions();
          
          setFormFields(updatedFields);
          setIsNewFieldAdded(true);
        }
      } catch (error) {
        //console.error('Error fetching options:', error);
      }
    };

    fetchData();

  }, [formFields, isNewFieldAdded, ApplicationDropDown, ModuleDropDown]); 

  ApplicationDropDown.onChange = async (selectedOptionId, updatedFields) => { 
    const moduleField = updatedFields.find((field) => field.id === 'module_id');
    
    const updatedFormFields = updatedFields.map((field) => {
      if (field.id === 'application_id') {
        return { ...field, value: selectedOptionId };
      }
      return field;
    });

    if (moduleField) {
      await moduleField.fetchOptions(selectedOptionId);
    } 
    setFormFields([...updatedFormFields]);
  };

  const getAllTasksData = async () => {
      try {
          await axiosPrivate.get(TaskApi).then((resp) => {
              setAllData(resp.data);
              setIsLoading(false);
          });
      } catch (error) {
          setIsLoading(false);
      }
  };

  useEffect(() => {
    const abortController = new AbortController();
    
    getAllTasksData();

    return () => {
      abortController.abort();
    };
  }, [setAllData]);

  const handleView = (row) => {
    setViewModalOpen(true);
    setEditMode(false);
    setSelectedRowData(row);
  };

  const handleEdit = (row) => {
    // Handle the Edit action
    setViewModalOpen(true);
    setEditMode(true);
    row.application_id = row.module.application_id;
    ApplicationDropDown.onChange(row.application_id, formFields);
    setSelectedRowData(row);
  };

  const handleEditRec = async (id, editRecord) => {
    const editRecordTemp = {
      module_id: editRecord.module_id,
      name: editRecord.name,
      code: editRecord.code,
      icon: editRecord.icon,
      description: editRecord.description,
      visibility: editRecord.visibility,
      external_url: editRecord.external_url,
      is_active: editRecord.is_active,
    };

    try {
      await axiosPrivate.put(`${TaskApi}/${id}`, editRecordTemp).then((resp) => {
        const updatedData = allData.map((row) =>
          row.id === selectedRowData.id ? { ...row, ...resp.data } : row
        );

        setAllData(updatedData);

        toast.success(`${resp.data.name} is successfully updated.`)
        handleCloseModal()
      });
    } catch (error) {
      if (error.response && error.response.status === 422) {
        toast.error('Validation Errors');
      } else if (error.response && error.response.status === 400) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Form submission error');
      }
      throw error;
    }
  };

  const memoizedHandleEdit = useMemo(() => handleEditRec, [allData, selectedRowData]);

  const handleCloseModal = () => {
    setViewModalOpen(false);
  };

  const handleDelete = async (row) => {
    // Prompt for confirmation
    const shouldDelete = window.confirm('Are you sure you want to delete this record?');

    if (!shouldDelete) {
      return;
    }

    try {
      const response = await axiosPrivate.delete(`${TaskApi}/${row.id}`);

      if (response.status === 204) {
        setAllData((prevData) => prevData.filter((item) => item.id !== row.id));
        toast.success('Record Delete successfully');
      } else {
        toast.error('Delete request failed:', response.statusText);
      }
    } catch (error) {
      console.error('Error deleting record:', error);
    }
  };

  const addBtn = `${process.env.PUBLIC_URL}/access-control/tasks/create/${layoutURL}`;
  const tableColumns = [
    {
      name: `${Application}`,
      selector: row => `${row.module.application.name}`,
      sortable: true,
      center: false,
    },
    {
      name: `${Module}`,
      selector: row => `${row.module.name}`,
      sortable: true,
      center: false,
    },
    {
      name: `${Task}`,
      selector: row => `${row.name}`,
      sortable: true,
      center: false,
    },
    {
      name: `${Status}`,
      selector: row => row.is_active,
      sortable: true,
      center: false,
      cell: row => (row.is_active ? <span className="badge badge-light-success">Active</span> : <span className="badge badge-light-danger">Inactive</span>)
    },
    {
      name: `${DateCreated}`,
      selector: row => `${formatDateWithTimeZone(row.created_at)}`,
      sortable: true,
      center: false,
    },
    {
      name: `${DateModified}`,
      selector: row => `${formatDateWithTimeZone(row.updated_at)}`,
      sortable: true,
      center: false,
    },
  ];

  return (
    <Fragment>
      <Breadcrumbs parent="Access Control" title="Tasks" mainTitle="Tasks" />
      <Container fluid={true}>
        <Row>
          <Col sm="12">
            <Card>
              <HeaderCard title="List" btnUrl={addBtn} />
              <CardBody>
                <DataTableComponent
                  tabledata={allData}
                  tableColumns={tableColumns}
                  isLoading={isLoading}
                  onView={handleView}
                  onDelete={handleDelete}
                  onEdit={handleEdit}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>

        <ViewModal
          isOpen={viewModalOpen}
          toggle={() => setViewModalOpen(!viewModalOpen)}
          rowData={selectedRowData}
          formFields={formFields}
          editMode={editMode}
          onEdit={memoizedHandleEdit}
        >
          {selectedRowData && (
            <>
              <Table>
                <tbody>
                  <tr className='border-bottom-primary'>
                    <th scope='row'>{Application}</th>
                    <td>{selectedRowData.module.application.name}</td>
                  </tr>
                  <tr className='border-bottom-primary'>
                    <th scope='row'>{Module}</th>
                    <td>{selectedRowData.module.name}</td>
                  </tr>
                  <tr className='border-bottom-secondary'>
                    <th scope='row'>{Task}</th>
                    <td>{selectedRowData.name}</td>
                  </tr>
                  <tr className='border-bottom-secondary'>
                    <th scope='row'>{Code}</th>
                    <td>{selectedRowData.code}</td>
                  </tr>
                  <tr className='border-bottom-secondary'>
                    <th scope='row'>{Description}</th>
                    <td>{selectedRowData.description}</td>
                  </tr>
                  <tr className='border-bottom-primary'>
                    <th scope='row'>{Icon}</th>
                    <td>{selectedRowData.icon}</td>
                  </tr>
                  <tr className='border-bottom-primary'>
                    <th scope='row'>{ExternalUrl}</th>
                    <td>{selectedRowData.external_url}</td>
                  </tr>
                  <tr className='border-bottom-secondary'>
                    <th scope='row'>{Visibility}</th>
                    <td>{selectedRowData.visibility ? <span className="badge badge-light-success">Visible</span> : <span className="badge badge-light-danger">Not Visible</span>}</td>
                  </tr>
                  <tr className='border-bottom-secondary'>
                    <th scope='row'>{Status}</th>
                    <td>{selectedRowData.is_active ? <span className="badge badge-light-success">Active</span> : <span className="badge badge-light-danger">Inactive</span>}</td>
                  </tr>
                  <tr className='border-bottom-primary'>
                    <th scope='row'>{DateCreated}</th>
                    <td>{formatDateWithTimeZone(selectedRowData.created_at)}</td>
                  </tr>
                  <tr className='border-bottom-secondary'>
                    <th scope='row'>{DateModified}</th>
                    <td>{formatDateWithTimeZone(selectedRowData.updated_at)}</td>
                  </tr>
                </tbody>
              </Table>
            </>
          )}

        </ViewModal>
      </Container>
    </Fragment>
  );
};

export default TaskList;